import React, { useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';

import { getRotaOverrideEditor, saveRotaOverride } from './rotaOverridesApi.js';
import './RotaAddCoverLauncher.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

function getTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createModalState() {
  return {
    open: false,
    overrideDate: getTodayIsoDate(),
    loading: false,
    saving: false,
    loaded: false,
    slots: [],
    staff: [],
    selectedBlockId: '',
    selectedUserId: '',
  };
}

export default function RotaAddCoverLauncher({
  rota = null,
  triggerClassName = '',
  children = 'Add Cover',
  disabled = false,
}) {
  const [modalState, setModalState] = useState(createModalState);

  const selectedCoverSlot = modalState.slots.find(
    (slot) => slot.blockId === modalState.selectedBlockId,
  ) || null;

  function closeModal() {
    setModalState(createModalState());
  }

  async function loadCoverEditor(overrideDate, preferredBlockId = '') {
    const masterRotaId = String(rota?.masterRotaId || '').trim();
    if (!masterRotaId || !overrideDate) {
      return;
    }

    setModalState((currentValue) => ({
      ...currentValue,
      loading: true,
      loaded: false,
      slots: [],
      staff: [],
      selectedBlockId: '',
      selectedUserId: '',
    }));

    try {
      const response = await getRotaOverrideEditor(masterRotaId, overrideDate);
      const loadedSlots = Array.isArray(response?.slots) ? response.slots : [];
      const loadedStaff = Array.isArray(response?.staff) ? response.staff : [];
      const resolvedSelectedBlockId = preferredBlockId && loadedSlots.some((slot) => slot.blockId === preferredBlockId)
        ? preferredBlockId
        : (loadedSlots[0]?.blockId || '');
      const selectedSlot = loadedSlots.find((slot) => slot.blockId === resolvedSelectedBlockId);
      const selectedUserId = String(selectedSlot?.effectiveAssignment?.userId || '').trim();

      setModalState((currentValue) => ({
        ...currentValue,
        loading: false,
        loaded: true,
        slots: loadedSlots,
        staff: loadedStaff,
        selectedBlockId: resolvedSelectedBlockId,
        selectedUserId,
      }));
    } catch (error) {
      console.error('Failed to load rota override editor', error);
      toast.error(`❌ Failed to load cover slots for ${rota?.rotaName || 'this rota'}.`, TOAST_CONFIG);
      setModalState((currentValue) => ({
        ...currentValue,
        loading: false,
        loaded: false,
        slots: [],
        staff: [],
        selectedBlockId: '',
        selectedUserId: '',
      }));
    }
  }

  function openModal() {
    setModalState({
      open: true,
      overrideDate: getTodayIsoDate(),
      loading: false,
      saving: false,
      loaded: false,
      slots: [],
      staff: [],
      selectedBlockId: '',
      selectedUserId: '',
    });
  }

  async function handleLoadCoverSlots() {
    if (!modalState.overrideDate) {
      return;
    }
    await loadCoverEditor(modalState.overrideDate);
  }

  async function handleSaveCoverOverride() {
    const masterRotaId = String(rota?.masterRotaId || '').trim();
    const overrideDate = String(modalState.overrideDate || '').trim();
    const blockId = String(modalState.selectedBlockId || '').trim();
    const userId = String(modalState.selectedUserId || '').trim();
    if (!masterRotaId || !overrideDate || !blockId || !userId) {
      toast.error('❌ Choose a date, slot, and cover user before saving.', TOAST_CONFIG);
      return;
    }

    setModalState((currentValue) => ({
      ...currentValue,
      saving: true,
    }));

    try {
      const response = await saveRotaOverride({
        masterRotaId,
        overrideDate,
        blockId,
        userId,
      });
      toast.success(
        `✅ Cover saved for ${response?.displayName || 'the selected user'}.`,
        TOAST_CONFIG,
      );
      await loadCoverEditor(overrideDate, blockId);
    } catch (error) {
      console.error('Failed to save rota override', error);
      toast.error('❌ Failed to save the cover override.', TOAST_CONFIG);
      setModalState((currentValue) => ({
        ...currentValue,
        saving: false,
      }));
    } finally {
      setModalState((currentValue) => ({
        ...currentValue,
        saving: false,
      }));
    }
  }

  return (
    <>
      <button
        className={triggerClassName}
        type='button'
        onClick={openModal}
        disabled={disabled || !rota?.masterRotaId}
      >
        {children}
      </button>

      {modalState.open && (
        <div className='rotaAddCover_modalOverlay' onClick={closeModal}>
          <div
            className='rotaAddCover_modal'
            role='dialog'
            aria-modal='true'
            onClick={(event) => event.stopPropagation()}
          >
            <div className='rotaAddCover_modalHeader'>
              <div className='rotaAddCover_modalHeaderText'>
                <h2 className='rotaAddCover_modalTitle'>Add Cover</h2>
                <p className='rotaAddCover_modalSubtitle'>
                  {rota?.rotaName || 'Selected rota'}
                </p>
              </div>
              <button
                className='rotaAddCover_closeButton'
                type='button'
                onClick={closeModal}
                aria-label='Close modal'
              >
                &times;
              </button>
            </div>

            <div className='rotaAddCover_toolbar'>
              <label className='rotaAddCover_field'>
                <span>Date</span>
                <input
                  type='date'
                  value={modalState.overrideDate}
                  onChange={(event) => (
                    setModalState((currentValue) => ({
                      ...currentValue,
                      overrideDate: event.target.value,
                    }))
                  )}
                />
              </label>
              <button
                className='rotaAddCover_actionButton'
                type='button'
                onClick={handleLoadCoverSlots}
                disabled={modalState.loading || !modalState.overrideDate}
              >
                {modalState.loading ? 'Loading...' : 'Load Slots'}
              </button>
            </div>

            {!modalState.loaded && !modalState.loading && (
              <p className='rotaAddCover_text'>
                Choose a date and load the rota slots for that day.
              </p>
            )}

            {modalState.loading ? (
              <div className='rotaAddCover_loadingState'>
                <CgSpinner className='rotaAddCover_spinner' />
                <p>Loading cover slots...</p>
              </div>
            ) : modalState.loaded && (
              <div className='rotaAddCover_layout'>
                <div className='rotaAddCover_slots'>
                  {modalState.slots.length === 0 ? (
                    <p className='rotaAddCover_text'>
                      No rota slots apply to the selected date.
                    </p>
                  ) : (
                    modalState.slots.map((slot) => {
                      const effectiveDisplayName = String(slot?.effectiveAssignment?.displayName || '').trim();
                      const baseDisplayName = String(slot?.baseAssignment?.displayName || '').trim();

                      return (
                        <button
                          className={`rotaAddCover_slotButton${modalState.selectedBlockId === slot.blockId ? ' rotaAddCover_slotButton--active' : ''}`}
                          key={slot.blockId}
                          type='button'
                          onClick={() => (
                            setModalState((currentValue) => ({
                              ...currentValue,
                              selectedBlockId: slot.blockId,
                              selectedUserId: String(slot?.effectiveAssignment?.userId || '').trim(),
                            }))
                          )}
                        >
                          <div className='rotaAddCover_slotHeader'>
                            <strong>{slot.period || 'Slot'}</strong>
                            <span>{slot.start} - {slot.end}</span>
                          </div>
                          <p className='rotaAddCover_slotText'>
                            Current: {effectiveDisplayName || 'Unassigned'}
                          </p>
                          {slot.hasOverride && (
                            <p className='rotaAddCover_slotMeta'>
                              Override replaces {baseDisplayName || 'an unassigned slot'}
                            </p>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>

                {selectedCoverSlot && (
                  <div className='rotaAddCover_editor'>
                    <h3>{selectedCoverSlot.period || 'Selected slot'}</h3>
                    <p className='rotaAddCover_text'>
                      {selectedCoverSlot.start} - {selectedCoverSlot.end}
                    </p>
                    <p className='rotaAddCover_text'>
                      Base assignment: {selectedCoverSlot?.baseAssignment?.displayName || 'Unassigned'}
                    </p>
                    {selectedCoverSlot.hasOverride && (
                      <p className='rotaAddCover_slotMeta'>
                        Existing override: {selectedCoverSlot?.effectiveAssignment?.displayName || 'Unassigned'}
                      </p>
                    )}

                    <label className='rotaAddCover_field'>
                      <span>Covering user</span>
                      <select
                        value={modalState.selectedUserId}
                        onChange={(event) => (
                          setModalState((currentValue) => ({
                            ...currentValue,
                            selectedUserId: event.target.value,
                          }))
                        )}
                      >
                        <option value=''>Select a user</option>
                        {modalState.staff.map((staffMember) => (
                          <option key={staffMember.cogId} value={staffMember.cogId}>
                            {staffMember.displayName}
                          </option>
                        ))}
                      </select>
                    </label>

                    <button
                      className='rotaAddCover_actionButton'
                      type='button'
                      onClick={handleSaveCoverOverride}
                      disabled={modalState.saving || !modalState.selectedUserId}
                    >
                      {modalState.saving ? 'Saving...' : 'Save Cover'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
